// Calls to Edge Functions. Every change goes through the actions endpoint with an operation ID
// (RK-SYS-006 section 2); offline-capable changes go through the outbox instead (offline/outbox.ts).
import type { ActionCode, ActionResult } from "@rk/shared";
import { accessToken, fetchWithTimeout, supabase } from "./supabase";
import { functionsUrl, env, regionHeaders } from "./env";
import { newId } from "./ids";
import { recordClientError } from "./errors";

export class ApiError extends Error {
  constructor(message: string, public code: string, public status: number) { super(message); }
}

type StreamEvent = { type: string; text?: string; message?: string };

/** Adds the signed-in person's access token, if there is one. */
async function withToken(headers: Record<string, string>): Promise<void> {
  const token = await accessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
}

/** One POST to a function; no answer at all is the "network" error. */
async function post(path: string, headers: Record<string, string>, body: BodyInit, timeoutMs: number): Promise<Response> {
  try {
    return await fetchWithTimeout(functionsUrl(path), { method: "POST", headers, body }, timeoutMs);
  } catch {
    throw new ApiError("There is no connection to the server.", "network", 0);
  }
}

/** A failed status is an error, unless the body is the endpoint's own `ok: false` answer, which callers read as it is. */
function throwIfFailed(res: Response, data: unknown): void {
  if (!res.ok && (data as ActionResult).ok !== false) {
    throw new ApiError((data as { message?: string }).message ?? "Something went wrong on the server.", (data as { code?: string }).code ?? "server", res.status);
  }
}

/** onStatus: told the HTTP status of the answer (Decision 84 #1: the upload loop tells a server error from a refusal). */
export async function callFunction<T = Record<string, unknown>>(path: string, body?: unknown, init: { auth?: boolean; form?: FormData; onStatus?: (status: number) => void } = {}): Promise<T> {
  const headers: Record<string, string> = { apikey: env.supabaseKey, ...regionHeaders() };
  if (init.auth !== false) await withToken(headers);
  if (!init.form) headers["Content-Type"] = "application/json";
  const res = await post(path, headers, init.form ?? JSON.stringify(body ?? {}), init.form ? 120_000 : 45_000);
  init.onStatus?.(res.status);
  const data = await res.json().catch(() => ({}));
  throwIfFailed(res, data);
  if (res.status === 401 && init.auth !== false) window.dispatchEvent(new CustomEvent("rk:session-expired"));
  return data as T;
}

/** What one event of the stream means for the result: "done" is the answer, "error" is a refusal; others are progress. */
function resultOf<T>(e: StreamEvent, onEvent: (e: StreamEvent) => void): T | undefined {
  if (e.type === "done") return e as unknown as T;
  if (e.type === "error") return { ok: false, message: e.message } as unknown as T;
  onEvent(e);
  return undefined;
}

/** The events of a server-sent-events stream, read to its end; the last "done" or "error" event is the result. */
async function readStream<T>(body: ReadableStream<Uint8Array>, onEvent: (e: StreamEvent) => void): Promise<T | null> {
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buffer = "";
  let done: T | null = null;
  for (;;) {
    const chunk = await reader.read().catch(() => ({ done: true, value: undefined }));
    if (chunk.value) buffer += dec.decode(chunk.value, { stream: true });
    let cut: number;
    while ((cut = buffer.indexOf("\n\n")) >= 0) {
      const line = buffer.slice(0, cut).replace(/^data: /, "");
      buffer = buffer.slice(cut + 2);
      let e: StreamEvent | null = null;
      try { e = JSON.parse(line); } catch { e = null; }
      if (!e) continue;
      done = resultOf<T>(e, onEvent) ?? done;
    }
    if (chunk.done) break;
  }
  return done;
}

/**
 * A call whose answer is sent as it is made (server-sent events, the RK Intelligence chat): `onEvent` sees each
 * progress event; the result is the final "done" event. A plain JSON answer (a refusal, a paused budget) is
 * returned as it is, so callers handle both the same way.
 */
export async function streamFunction<T = Record<string, unknown>>(path: string, body: Record<string, unknown>, onEvent: (e: { type: string; text?: string }) => void): Promise<T> {
  const headers: Record<string, string> = { apikey: env.supabaseKey, "Content-Type": "application/json", Accept: "text/event-stream", ...regionHeaders() };
  await withToken(headers);
  const res = await post(path, headers, JSON.stringify({ ...body, stream: true }), 120_000);
  if (res.status === 401) window.dispatchEvent(new CustomEvent("rk:session-expired"));
  if (!res.body || !(res.headers.get("Content-Type") ?? "").includes("text/event-stream")) {
    const data = await res.json().catch(() => ({}));
    throwIfFailed(res, data);
    return data as T;
  }
  const done = await readStream<T>(res.body, onEvent);
  if (!done) throw new ApiError("The answer was cut off. Try again.", "network", 0);
  return done;
}

export type PerformOptions = { operationId?: string; deviceId?: string; recordedAt?: string };

/** The same JSON whatever the order of an object's keys, so a payload built afresh compares equal. */
function sameJson(value: unknown): string {
  return JSON.stringify(value, (_k, v: unknown) => v && typeof v === "object" && !Array.isArray(v)
    ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
    : v) ?? "";
}

// Owner's decision 85 #1: a change whose outcome the app does not know (the server answered "timeout, retry_safe", a
// 5xx came back that is not the actions endpoint's own `ok: false` answer - a gateway's 502 or 504, Supabase's 546, a
// 500 that is not JSON - or no answer came at all) is sent again with the SAME operation_id when the same action is
// sent with an equal payload, so the database runs it once or answers from the stored result: it never posts a
// payment, an arrival or a credit twice. A definite answer (a success or a refusal) forgets it; anything else gets a
// new operation_id. The memory belongs to the signed-in person (the user of the access token): it is never used for
// another person, it is cleared when anyone signs out or a different person's session is reported, and an entry
// expires 10 minutes after the unknown answer (after that a press is a new decision).
type Unsettled = { operationId: string; user: string | null; at: number };
const unsettled = new Map<string, Unsettled>();
const UNSETTLED_MAX = 20;
const UNSETTLED_FOR_MS = 10 * 60_000;

/** The user (the JWT's sub) an access token belongs to; null when there is none or it cannot be read. */
function userOf(token: string | null): string | null {
  try {
    const part = token?.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    const sub = (JSON.parse(atob(b64)) as { sub?: unknown }).sub;
    return typeof sub === "string" ? sub : null;
  } catch {
    return null;
  }
}

// Cleared on every sign-out (the Sign out button, the 12-hour limit and an ended session all go through
// supabase.auth.signOut) and when a session for a different person is reported. A client without the listener (a test
// double) only loses the clearing; the per-person check below still holds.
try {
  supabase?.auth?.onAuthStateChange?.((event, session) => {
    const user = session?.user?.id ?? null;
    if (event === "SIGNED_OUT") unsettled.clear();
    else if (user !== null) for (const [k, v] of unsettled) if (v.user !== user) unsettled.delete(k);
  });
} catch {
  // no auth client here
}

/** The operation id an earlier, unsettled attempt of the same action by the same person left, while it is still fresh. */
function unsettledId(key: string, user: string | null): string | undefined {
  const kept = unsettled.get(key);
  return kept && kept.user === user && Date.now() - kept.at <= UNSETTLED_FOR_MS ? kept.operationId : undefined;
}

/** A failed attempt whose outcome may not be known keeps its id for a retry; any other outcome forgets it. */
function settleAttempt(key: string, mine: { operationId: string; user: string | null }, r: ActionResult, unknown: boolean): ActionResult {
  unsettled.delete(key);
  if (r.ok === false && (unknown || r.retry_safe === true || r.code === "network")) {
    unsettled.set(key, { ...mine, at: Date.now() });
    if (unsettled.size > UNSETTLED_MAX) unsettled.delete(unsettled.keys().next().value as string);
  }
  return r;
}

export async function performOnline(code: ActionCode, payload: unknown, opts: PerformOptions = {}): Promise<ActionResult> {
  const key = `${code} ${sameJson(payload)}`;
  const user = opts.operationId === undefined ? userOf(await accessToken().catch(() => null)) : null;
  const operationId = opts.operationId ?? unsettledId(key, user) ?? newId();
  const settle = (r: ActionResult, unknown = false): ActionResult => opts.operationId === undefined ? settleAttempt(key, { operationId, user }, r, unknown) : r;
  try {
    return settle(await callFunction<ActionResult>(`actions/${code}`, {
      payload,
      operation_id: operationId,
      device_id: opts.deviceId,
      recorded_at: opts.recordedAt
    }));
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.code === "server") recordClientError(e.message, { action: code });
      // callFunction throws only when the reply is not the endpoint's own `ok: false` answer: a 5xx then is unknown.
      return settle({ ok: false, code: e.code, message: e.message }, e.status >= 500);
    }
    throw e;
  }
}

// Owner's decision 37 (BD-160): wakes a server function when chat opens, so the first question does not wait for a
// cold start. A ping only (no data, no model call); at most once in five minutes; a failure is ignored.
let warmedAt = 0;
export function warmFunction(path: string): void {
  if (Date.now() - warmedAt < 5 * 60_000) return;
  warmedAt = Date.now();
  void fetchWithTimeout(functionsUrl(path), { method: "POST", headers: { apikey: env.supabaseKey, "Content-Type": "application/json", ...regionHeaders() }, body: "{}" }, 10_000).catch(() => undefined);
}
